import React, { useState, useEffect, useMemo } from "react";
import {
  getBulkUploadRequests,
  getBulkUploadTemplates,
} from "../api/documents";
import toast from "react-hot-toast";
import Pagination from "./Pagination";
import { formatForDisplay } from "../utils/gmtUtils";
import {
  DocumentIcon,
  ArrowPathIcon,
  XMarkIcon,
  FunnelIcon,
  DocumentTextIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { hasDocumentTypeAccess } from "../utils/permissions";
import {
  Button,
  Card,
  DataGrid,
  Dropdown,
  TableSkeleton,
  EmptyDocumentsIllustration,
} from "../components/ui";

const BulkSendRequests = () => {
  const [bulkSendRequests, setBulkSendRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const itemsPerPage = 10;
  const { permissions } = useAuth();

  // Filter templates based on document type permissions
  const filteredTemplates = useMemo(() => {
    if (!permissions || !permissions.templates) {
      return templates;
    }

    return templates.filter((template) => {
      const documentType = template.documentType || "";
      return hasDocumentTypeAccess(permissions, documentType);
    });
  }, [templates, permissions]);

  const pillColors = {
    PENDING: "bg-amber-100 text-amber-800",
    COMPLETED: "bg-emerald-100 text-emerald-800",
    FAILED: "bg-red-100 text-red-800",
    ALREADY_SENT: "bg-blue-100 text-blue-800",
    DUPLICATE_FOR_SAME_TEMPLATE: "bg-orange-100 text-orange-800",
  };

  const statusTextMapped = {
    PENDING: "In Progress",
    COMPLETED: "Sent",
    FAILED: "Failed",
    ALREADY_SENT: "Already Sent",
    DUPLICATE_FOR_SAME_TEMPLATE: "Duplicate",
  };

  const fetchTemplates = async () => {
    try {
      const response = await getBulkUploadTemplates();
      setTemplates(response);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const fetchBulkSendRequests = async () => {
    try {
      setIsLoading(true);

      // Create request parameters
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      // Add template IDs if selected
      if (selectedTemplates.length > 0) {
        params.templateIds = selectedTemplates.join(",");
      }

      // Add statuses if selected
      if (selectedStatuses.length > 0) {
        params.statuses = selectedStatuses.join(",");
      }

      const response = await getBulkUploadRequests(params);

      setBulkSendRequests(response.bulkSendRequests);
      setTotalPages(response.totalPages);
      setTotalItems(response.total);
      setCurrentPage(response.page);
    } catch (error) {
      console.error("Error fetching bulk send requests:", error);
      toast.error(error.message || "Failed to fetch bulk send requests");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    fetchBulkSendRequests();
  }, [currentPage, selectedTemplates, selectedStatuses]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const clearFilters = () => {
    setSelectedTemplates([]);
    setSelectedStatuses([]);
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const columns = [
    {
      key: "templateName",
      label: "Template",
      sortable: false,
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <DocumentTextIcon className="h-4 w-4 text-primary" />
          </div>
          <span className="font-medium text-gray-900 truncate max-w-[180px]">
            {row.templateId?.name || "N/A"}
          </span>
        </div>
      ),
    },
    {
      key: "recipientsName",
      label: "Recipient",
      sortable: false,
      render: (_, row) => (
        <span className="font-medium text-gray-900">{row.recipientsName}</span>
      ),
    },
    {
      key: "recipientsEmail",
      label: "Email",
      sortable: false,
      render: (_, row) => (
        <a
          href={`mailto:${row.recipientsEmail}`}
          className="text-primary hover:text-primary-dark hover:underline truncate block max-w-[200px]"
        >
          {row.recipientsEmail}
        </a>
      ),
    },
    {
      key: "recipientRole",
      label: "Role",
      sortable: false,
      render: (_, row) => (
        <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 capitalize">
          {row.recipientRole}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortable: false,
      render: (_, row) => (
        <span
          className={`px-2.5 py-1 inline-flex items-center rounded-lg text-xs font-medium ${
            pillColors[row.status] || "bg-gray-100 text-gray-700"
          }`}
        >
          {statusTextMapped[row.status] || row.status}
        </span>
      ),
    },
    {
      key: "expiryDateTime",
      label: "Expiry",
      sortable: false,
      render: (_, row) => (
        <span className="text-sm text-gray-600 whitespace-nowrap">
          {formatForDisplay(row.expiryDateTime, "MMM D, YYYY h:mm a")}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Created",
      sortable: false,
      render: (_, row) => (
        <span className="text-sm text-gray-600 whitespace-nowrap">
          {formatForDisplay(row.createdAt, "MMM D, YYYY h:mm a")}
        </span>
      ),
    },
  ];

  const tableData = bulkSendRequests.map((r) => ({
    ...r,
    id: r._id,
  }));

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card className="animate-pulse">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-5 w-20 bg-gray-200 rounded-lg" />
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="h-12 w-52 bg-gray-200 rounded-xl" />
            <div className="h-12 w-44 bg-gray-200 rounded-xl" />
            <div className="h-10 w-24 bg-gray-200 rounded-lg" />
          </div>
        </Card>
        <Card padding="none" className="overflow-hidden">
          <TableSkeleton rows={10} cols={7} showHeader />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter Section */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 pt-5">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-4 sm:gap-6 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <Dropdown
              label="Templates"
              options={filteredTemplates.map((t) => ({
                value: t._id,
                label: t.name,
              }))}
              value={selectedTemplates}
              onChange={(vals) => {
                setSelectedTemplates(vals);
                setCurrentPage(1);
              }}
              multiple
              emptyLabel="All Templates"
              selectedCountLabel={(count) =>
                `${count} template${count === 1 ? "" : "s"} selected`
              }
              className="min-w-[200px] flex-1"
            />
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <Dropdown
              label="Status"
              options={Object.entries(statusTextMapped).map(
                ([status, text]) => ({
                  value: status,
                  label: text,
                }),
              )}
              value={selectedStatuses}
              onChange={(vals) => {
                setSelectedStatuses(vals);
                setCurrentPage(1);
              }}
              multiple
              emptyLabel="All Status"
              selectedCountLabel={(count) =>
                `${count} status${count === 1 ? "" : "es"} selected`
              }
              className="min-w-[180px] flex-1"
            />
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {(selectedTemplates.length > 0 || selectedStatuses.length > 0) && (
            <Button
              variant="secondary"
              onClick={clearFilters}
              leftIcon={XMarkIcon}
            >
              Clear Filters
            </Button>
          )}

          <Button
            variant="primary"
            onClick={() => {
              setCurrentPage(1);
              fetchBulkSendRequests();
            }}
            disabled={isLoading}
            leftIcon={ArrowPathIcon}
            className={isLoading ? "[&>svg]:animate-spin" : ""}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Results Section */}
      <div>
        <DataGrid
          columns={columns}
          data={tableData}
          headerVariant="subtle"
          emptyMessage="No bulk send requests found. Create your first bulk send from the Create Bulk Send tab."
          emptyIllustration={EmptyDocumentsIllustration}
        />
        {totalPages > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
};

export default BulkSendRequests;
